import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { getDualSpendDisplay } from "@/features/opportunities/lib/dual-spend-display";
import { formatPct } from "@/features/opportunities/lib/format-opportunity";
import type { OpportunityAction } from "@/features/opportunities/types/opportunities.types";

import { personaOpportunityStatusLabel } from "../../../lib/persona-opportunity-tab-status.js";
import type { HubChromeOpportunityRow } from "../../page/PersonaOpportunityHubChrome/hub-chrome.types.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";

const OPPORTUNITY_ACTIONS: readonly OpportunityAction[] = ["launch", "scale", "test"];

const ACTION_TEXT: Record<OpportunityAction, string> = {
  launch: "text-status-success",
  scale: "text-primary",
  test: "text-status-warning",
};

const ACTION_LABEL: Record<OpportunityAction, { key: string; fallback: string }> = {
  launch: { key: "opportunities.actions.launch", fallback: "Launch" },
  scale: { key: "opportunities.actions.scale", fallback: "Scale" },
  test: { key: "opportunities.actions.test", fallback: "Test" },
};

export interface CompetitorOpportunityCardProps {
  row: HubChromeOpportunityRow;
  selected?: boolean;
  selectionEnabled?: boolean;
  onToggleSelect?: (opportunityId: string) => void;
  /** When set, Generate scripts is enabled and opens that flow for this card. */
  onGenerateScripts?: (opportunityId: string) => void;
}

export function CompetitorOpportunityCard({
  row,
  selected = false,
  selectionEnabled = false,
  onToggleSelect,
  onGenerateScripts,
}: CompetitorOpportunityCardProps) {
  const { t } = useTranslation();
  const notReady = t(
    "personaOpportunities.workspace.actionNotReady",
    "Not available yet — coming in a later release.",
  );
  const title = row.topicTitle ?? row.angle;
  const description = row.topicDescription ?? row.findings;
  const showFindingsRow = Boolean(row.topicDescription && row.findings);
  const action = asOpportunityAction(row.action);
  const actionLabel = action
    ? t(ACTION_LABEL[action].key, ACTION_LABEL[action].fallback)
    : row.action?.trim() || t("opportunities.actions.none", "No action");
  const meta = [row.feature, row.subFeature, row.dataset, row.engine].filter((part): part is string =>
    Boolean(part?.trim()),
  );

  return (
    <article className="flex flex-col gap-5 rounded-md border border-border bg-card p-[22px] transition-shadow hover:shadow-md">
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex items-start gap-3">
          <PersonaOpportunitySelectCheckbox
            checked={selected}
            disabled={!selectionEnabled}
            onToggle={() => onToggleSelect?.(row.id)}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold leading-[1.35] text-foreground">{title}</h3>
              <span className="shrink-0 rounded-sm border border-input bg-background px-1.5 py-0.5 text-[11px] font-medium">
                {personaOpportunityStatusLabel(t, row.status)}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className={cn("font-semibold", action ? ACTION_TEXT[action] : undefined)}>
                {actionLabel}
              </span>
              {meta.map((part, index) => (
                <span key={`${part}-${index}`}>
                  {" · "}
                  <span>{part}</span>
                </span>
              ))}
            </p>
          </div>
        </div>
        {description ? (
          <p className="line-clamp-3 text-[13px] text-muted-foreground" title={description}>
            {description}
          </p>
        ) : null}
        <div className="space-y-1.5 text-xs text-muted-foreground">
          {showFindingsRow && row.findings ? (
            <LabeledLine
              label={t("opportunities.table.findings", "Findings")}
              value={row.findings}
            />
          ) : null}
          {row.suggestion ? (
            <LabeledLine
              label={t("opportunities.table.suggestion", "Suggestion")}
              value={row.suggestion}
              highlighted
            />
          ) : null}
        </div>
        <CompetitorSpendBand row={row} />
      </div>

      <div className="grid grid-cols-2 gap-2 border-t border-border pt-4">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button type="button" variant="outline" size="sm" className="w-full" disabled>
                  {t("personaOpportunities.active.similarAngles", "See similar angles")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{notReady}</TooltipContent>
          </Tooltip>
          {onGenerateScripts ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={cn("w-full bg-primary/10 text-primary hover:bg-primary/15")}
              onClick={(event) => {
                event.stopPropagation();
                onGenerateScripts(row.id);
              }}
            >
              {t("personaOpportunities.active.generateScripts", "Generate scripts")}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className={cn("w-full bg-primary/10 text-primary hover:bg-primary/15")}
                    disabled
                  >
                    {t("personaOpportunities.active.generateScripts", "Generate scripts")}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{notReady}</TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>
    </article>
  );
}

function LabeledLine({
  label,
  value,
  highlighted = false,
}: {
  label: string;
  value: string;
  highlighted?: boolean;
}) {
  return (
    <p
      className={cn(
        "rounded-sm px-1.5 py-1",
        highlighted && "bg-primary/10 text-foreground",
      )}
    >
      <span className="font-medium uppercase tracking-wide text-muted-foreground/80">{label}</span>{" "}
      <span className={cn(highlighted && "font-semibold")}>{value}</span>
    </p>
  );
}

function CompetitorSpendBand({ row }: { row: HubChromeOpportunityRow }) {
  const { t } = useTranslation();
  const you = getDualSpendDisplay(row.spendPerAngle ?? null, row.spendUsd ?? null, 1);
  const benchmark = getDualSpendDisplay(
    row.benchmarkSpendPerAngle ?? null,
    row.benchmarkSpendUsd ?? null,
    1,
  );
  const competitors = getDualSpendDisplay(
    row.competitorSpendPerAngle ?? null,
    row.competitorSpendUsd ?? null,
    1,
  );
  const max = Math.max(you.barValue ?? 0, benchmark.barValue ?? 0, competitors.barValue ?? 0, 0);
  const widthOf = (value: number | null) =>
    value == null || max === 0 ? 0 : Math.min(100, (value / max) * 100);

  return (
    <div className="mt-auto space-y-3 border-t border-border pt-4">
      <div className="space-y-2">
        <SpendBar
          label={t("opportunities.card.you", "You")}
          value={you.primary}
          width={widthOf(you.barValue)}
          barClassName="bg-primary"
        />
        <SpendBar
          label={t("opportunities.card.benchmark", "Benchmark")}
          value={benchmark.primary}
          width={widthOf(benchmark.barValue)}
          barClassName="bg-violet-500/70"
        />
        <SpendBar
          label={t("opportunities.card.competitors", "Competitors")}
          value={competitors.primary}
          width={widthOf(competitors.barValue)}
          barClassName="bg-violet-500"
        />
      </div>
      <TooltipProvider delayDuration={200}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <KpiTile
            label={t("opportunities.table.spend", "My Spend")}
            value={you.primary}
            tooltip={t(
              "personaOpportunities.hub.opportunities.card.youSpendTooltip",
              "Your share of total creatives on this topic.",
            )}
          />
          <KpiTile
            label={t("opportunities.table.benchmarkSpend", "Benchmark Spend")}
            value={benchmark.primary}
            tooltip={t(
              "personaOpportunities.hub.opportunities.card.benchmarkSpendTooltip",
              "Benchmark share of total creatives on this topic.",
            )}
          />
          <KpiTile
            label={t("opportunities.table.competitorSpend", "Competitor Spend")}
            value={competitors.primary}
            tooltip={t(
              "personaOpportunities.hub.opportunities.card.competitorSpendTooltip",
              "Competitor share of total creatives on this topic.",
            )}
          />
          <KpiTile
            label={t("opportunities.table.kpi", "KPI")}
            value={row.kpi == null ? "—" : formatPct(row.kpi)}
            tooltip={t(
              "personaOpportunities.hub.opportunities.card.kpiTooltip",
              "The opportunity KPI for this topic.",
            )}
          />
        </div>
      </TooltipProvider>
    </div>
  );
}

function SpendBar({
  label,
  value,
  width,
  barClassName,
}: {
  label: string;
  value: string;
  width: number;
  barClassName: string;
}) {
  return (
    <div className="grid grid-cols-[4.5rem_1fr_3.25rem] items-center gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="h-2.5 overflow-hidden rounded-[5px] bg-muted">
        <div className={cn("h-full", barClassName)} style={{ width: `${width}%` }} />
      </div>
      <span className="text-right tabular-nums text-foreground">{value}</span>
    </div>
  );
}

function KpiTile({ label, value, tooltip }: { label: string; value: string; tooltip: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="min-w-0 rounded-md bg-muted/40 px-1.5 py-2 text-center sm:px-2">
          <div className="text-[19px] font-medium tabular-nums leading-none text-foreground">
            {value}
          </div>
          <div className="mt-1.5 flex items-center justify-center gap-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <span className="leading-tight">{label}</span>
            <Icon name="info" size="xs" className="size-2.5 shrink-0 opacity-60" aria-hidden />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[190px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function asOpportunityAction(action: string | null | undefined): OpportunityAction | null {
  return OPPORTUNITY_ACTIONS.find((known) => known === action) ?? null;
}
